import { FileNameSeparatorWithExtension } from '@/components/FileNameSeparatorWithExtension';
import type { ApprovingInvoiceReportColumn } from '@/context/services/approving_invoice_reports';
import { useConfirmApproveModal } from '@/features/approving_invoice_reports/components/ConfirmApproveModal/context/ConfirmApproveModalContext';
import { useConfirmRejectModal } from '@/features/approving_invoice_reports/components/ConfirmRejectModal/context/ConfirmRejectModalContext';
import { ActionCell } from '@/features/approving_invoice_reports/components/List/Table/Columns/Cells/components/ActionCell';
import { InfoTagCell } from '@/features/approving_invoice_reports/components/List/Table/Columns/Cells/components/InfoTagCell';
import { StatusCell } from '@/features/approving_invoice_reports/components/List/Table/Columns/Cells/components/StatusCell';
import { TooltipCell } from '@/features/approving_invoice_reports/components/List/Table/Columns/Cells/components/TooltipCell';
import { SORT_DIRECTIONS } from '@/features/approving_invoice_reports/constants';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  Checkbox,
  IconDrawer,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { ColumnType } from 'antd/es/table/interface';
import classnames from 'classnames/bind';
import { format } from 'date-util';
import { useCallback, useMemo } from 'react';
import styles from './columns.module.scss';

const cx = classnames.bind(styles);

export const tableWidth = Object.entries(styles)
  .filter(([key]) => key.startsWith('column_') && key.endsWith('_width'))
  .map(([, value]) => Number(value))
  .reduce((pre, next) => {
    return pre + next;
  }, 0);

export type UseColumnsProps = {
  selectedRowKeys: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (record: ApprovingInvoiceReportColumn, checked: boolean) => void;
  isAllSelected: boolean;
  isAllDisabled: boolean;
  isLoading?: boolean;
};

export const useColumns = ({
  selectedRowKeys,
  onSelectAll,
  onSelectRow,
  isAllSelected,
  isAllDisabled,
  isLoading = false,
}: UseColumnsProps): ColumnType<ApprovingInvoiceReportColumn>[] => {
  const { t } = useTranslation();
  const container = useGlobalContainerRef();
  const { onOpenConfirmRejectModal } = useConfirmRejectModal();
  const { onOpenConfirmApproveModal } = useConfirmApproveModal();

  const sorterConfig = useMemo(
    () =>
      !isLoading
        ? {
            sorter: true,
            sortDirections: SORT_DIRECTIONS,
            showSorterTooltip: { mouseEnterDelay: 0.8 },
          }
        : { showSorterTooltip: false },
    [isLoading]
  );

  const handleOpenConfirmRejectModal = useCallback(
    (reportId: string) => () => {
      onOpenConfirmRejectModal(reportId);
    },
    [onOpenConfirmRejectModal]
  );

  const handleOpenConfirmApproveModal = useCallback(
    (reportId: string) => () => {
      onOpenConfirmApproveModal({ reportTarget: reportId });
    },
    [onOpenConfirmApproveModal]
  );

  const numberColumnMessageTypes = [
    'report_edoc_scanner_message',
    'duplicate_reports_message',
  ];

  const invoiceFileMessageTypes = ['invoice_file_edoc_scanner_message'];

  const onCheckboxChange = (
    e: CheckboxChangeEvent,
    record: ApprovingInvoiceReportColumn
  ) => {
    e.stopPropagation();
    onSelectRow(record, e.target.checked);
  };

  const onStopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const allColumns: ColumnType<ApprovingInvoiceReportColumn>[] = [
    {
      title: (
        <Checkbox
          // set color for disabledAll checkbox border box
          className={cx(`${isAllDisabled ? 'disabledCustomCheckbox' : ''}`)}
          indeterminate={selectedRowKeys.length > 0 && !isAllSelected}
          checked={isAllSelected}
          disabled={isAllDisabled || isLoading}
          onChange={(e) => onSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'select',
      key: 'select',
      fixed: 'left',
      onCell: () => ({
        onClick: onStopPropagation,
      }),
      render(_, record) {
        return (
          <TooltipCell
            title={
              !record.is_waiting
                ? `${record?.status?.display_name}${t(
                    'approving_invoice_reports.table.columns.application_number_not_approvable'
                  )}`
                : ''
            }
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            <Checkbox
              checked={selectedRowKeys.includes(record.id)}
              disabled={!record.is_waiting || isLoading}
              className={cx(
                `${!record.is_waiting ? 'disabledCustomCheckbox' : ''}`
              )}
              onChange={(e: CheckboxChangeEvent) => onCheckboxChange(e, record)}
            />
          </TooltipCell>
        );
      },
      align: 'center',
      width: `${styles['column_select_width']}px`,
      className: cx(styles['checkboxContainer']),
    },
    {
      title: t('approving_invoice_reports.table.columns.application_number'),
      dataIndex: 'number',
      key: 'number',
      fixed: 'left',
      render(value, record) {
        return (
          <InfoTagCell
            errorMessages={record.error_messages}
            messageType={numberColumnMessageTypes}
          >
            <div className={cx('numberContainer')}>
              {value}
              <Tooltip
                title={t('open_preview')}
                placement='top'
                getPopupContainer={container}
                overlayClassName={cx(styles['columnTooltip'])}
              >
                <div className={cx('iconWrapper')}>
                  <IconDrawer />
                </div>
              </Tooltip>
            </div>
            <StatusCell status={record.status} />
          </InfoTagCell>
        );
      },
      width: `${styles['column_number_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('approving_invoice_reports.table.columns.created_date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_created_at_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('approving_invoice_reports.table.columns.application_date'),
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_submitted_at_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('approving_invoice_reports.table.columns.applicant'),
      dataIndex: 'office_member',
      key: 'office_member',
      render(value) {
        if (!value) return '';

        return (
          <div className={cx(styles['applicantContainer'])}>
            <div className={cx(styles['applicantName'])}>
              {value.name || ''}
            </div>
            <div className={cx(styles['applicantId'])}>
              {value.number || ''}
            </div>
          </div>
        );
      },
      width: `${styles['column_office_member_width']}px`,
    },
    {
      title: t('approving_invoice_reports.table.columns.title'),
      dataIndex: 'title',
      key: 'title',
      render(value) {
        return (
          <TooltipCell
            title={value}
            className={cx(styles['title'])}
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            {value || ''}
          </TooltipCell>
        );
      },
    },
    {
      title: t('approving_invoice_reports.table.columns.payee'),
      dataIndex: 'ap_payee_name',
      key: 'ap_payee_name',
      render(value) {
        return (
          <TooltipCell
            title={value}
            className={cx(styles['payeeName'])}
            wrapperClassName={cx(styles['columnTooltip'])}
          >
            {value || ''}
          </TooltipCell>
        );
      },
    },
    {
      title: t('approving_invoice_reports.table.columns.expense_booking_date'),
      dataIndex: 'book_date',
      key: 'book_date',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_book_date_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('approving_invoice_reports.table.columns.payment_due_date'),
      dataIndex: 'due_date',
      key: 'due_date',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_due_date_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('approving_invoice_reports.table.columns.total_amount'),
      dataIndex: 'total_amount',
      key: 'total_amount',
      render(value, record) {
        return (
          <>
            <div>{value || ''}</div>
            {record.foreign_currency_amount && (
              <div className={cx(styles['foreignCurrencyAmount'])}>
                {record.foreign_currency_amount}
              </div>
            )}
          </>
        );
      },
      width: `${styles['column_total_amount_width']}px`,
      align: 'right',
    },
    {
      title: t('approving_invoice_reports.table.columns.approver'),
      dataIndex: 'report_approval_names',
      key: 'report_approval_names',
      render(value) {
        return (
          <TooltipCell
            title={value}
            wrapperClassName={cx(styles['columnTooltip'])}
            className={cx(styles['approverName'])}
          >
            {value || ''}
          </TooltipCell>
        );
      },
    },
    {
      title: t('approving_invoice_reports.table.columns.approval_date'),
      dataIndex: 'approved_at',
      key: 'approved_at',
      render: (value) => format(value, 'YYYY/MM/DD'),
      width: `${styles['column_approved_at_width']}px`,
      ...sorterConfig,
    },
    {
      title: t('approving_invoice_reports.table.columns.attached_invoice'),
      dataIndex: 'invoice_file',
      key: 'invoice_file',
      render(value, record) {
        if (!value) return '';

        return (
          <div className={cx(styles['invoiceFileContainer'])}>
            <TooltipCell
              title={value.name}
              wrapperClassName={cx(styles['columnTooltip'])}
            >
              <FileNameSeparatorWithExtension>
                {value.name}
              </FileNameSeparatorWithExtension>
            </TooltipCell>

            <InfoTagCell
              errorMessages={record.error_messages}
              messageType={invoiceFileMessageTypes}
              automaticStatusWaiting={record.automatic_status_waiting}
              timestampStatusCreating={record.timestamp_status_creating}
              receiptType={value.receipt_type}
            />
          </div>
        );
      },
      width: `${styles['column_invoice_file_width']}px`,
    },
    {
      render(_, record) {
        return (
          <ActionCell
            record={record}
            onOpenConfirmRejectModal={handleOpenConfirmRejectModal(record?.id)}
            onConfirmApprove={handleOpenConfirmApproveModal(record?.id)}
          />
        );
      },
      width: `${styles['column_action_width']}px`,
      ellipsis: false,
      align: 'center',
      fixed: 'right',
    },
  ];

  return allColumns;
};
